import { AppConfig } from '@ltvco/refresh-lib/ctx';
import { Box, Button, Text } from '@ltvco/refresh-lib/theme';
import { useAccount, useSession } from '@ltvco/refresh-lib/v1';
import {
  AuthRequiredModal,
  buildUserListingBody,
  buildUserListingUpdateBody,
  ContactSellerModal,
  isListingSavedByUser,
  resolveSaveAction,
  useMarketplaceListing,
  useSimilarMarketplaceListings,
  useUserListings,
  VehicleDetailsPage,
  type ListingLookup,
  type MarketplaceListing,
} from '@ltvco/refresh-lib/v2';
import { useContext, useState } from 'react';
import { useHref, useLocation, useNavigate, useParams } from 'react-router-dom';

import { ReportSectionSelfInspectionCTA } from 'components/SelfInspection/CTAs/ReportSectionSelfInspectionCTA/ReportSectionSelfInspectionCTA';
import { SelfInspectionResults } from 'components/SelfInspection/SelfInspectionResults/SelfInspectionResults';

const BUMPER_DOMAIN = 'https://www.bumper.com';
const BUILDING_REPORT_PATH = 'lp/8dc2dc/3/building-report';
const BUMPER_TERMS = 'https://www.bumper.com/terms';
const BUMPER_PRIVACY = 'https://www.bumper.com/privacy';
const SUBSCRIBE_URL = 'https://www.bumper.com/lp/8b3bc4/4/subscribe';

function openListingInNewTab(listingId: string): void {
  const prefix = (import.meta.env.VITE_PATH_PREFIX as string | undefined) ?? '';
  window.open(
    `${prefix}/marketplace/listing/${listingId}`,
    '_blank',
    'noopener',
  );
}

/**
 * Public vehicle detail page. Serves both `/marketplace/listing/:listingId`
 * and `/marketplace/vehicle/:vin`. Includes the Phase 2 contact-seller modal.
 */
export function MarketplaceVehicleDetail() {
  const { listingId, vin } = useParams<{ listingId?: string; vin?: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const backHref = useHref('/marketplace');
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  const [isAuthRequiredModalOpen, setIsAuthRequiredModalOpen] = useState(false);
  const { trackEvent } = useContext(AppConfig);
  const routeLocation = listingId ? 'listing' : 'vehicle';

  const {
    session: { isAuthenticated },
  } = useSession();

  const { data: accountData } = useAccount(isAuthenticated);
  const userInfo = accountData?.account.user_info;

  const lookup: ListingLookup = listingId
    ? { searchBy: 'listingID', listingID: listingId }
    : { searchBy: 'vin', vin: vin ?? '' };

  const { data: report, isLoading } = useMarketplaceListing(lookup);
  const listing = report?.listings?.[0] ?? null;
  const automobile =
    report?.automobiles?.find(
      (candidate: { vin?: string | null }) =>
        candidate.vin === listing?.vehicle?.vin,
    ) ?? null;

  const { listings: similarListings } = useSimilarMarketplaceListings(
    listing?.vehicle,
    listing?.id,
  );

  const { listingsQuery, createListingMutation, updateListingMutation } =
    useUserListings();
  const userListings = listingsQuery.data?.listings ?? [];

  const listingWithSaved: MarketplaceListing | null = listing
    ? {
        ...listing,
        saved: isListingSavedByUser(userListings, { id: listing.id ?? null }),
      }
    : listing;

  const closeAuthRequiredModal = (): void => setIsAuthRequiredModalOpen(false);

  const goToSignup = (): void => window.location.assign(SUBSCRIBE_URL);

  const handleToggleSave = (_nextSaved: boolean): void => {
    if (!listing) {
      return;
    }
    if (!isAuthenticated) {
      setIsAuthRequiredModalOpen(true);
      return;
    }
    trackEvent(routeLocation, 'Click | save | button');
    const plan = resolveSaveAction(userListings, { id: listing.id ?? null });
    if (plan.kind === 'update' && plan.userListingId !== null) {
      updateListingMutation.mutate({
        userListingId: plan.userListingId,
        body: buildUserListingUpdateBody(listing, plan.saved),
      });
    } else {
      createListingMutation.mutate({ body: buildUserListingBody(listing) });
    }
  };

  const openVinReport = () => {
    const vinValue = listing?.vehicle?.vin;
    if (!vinValue) {
      return;
    }
    const base = isAuthenticated
      ? `${BUMPER_DOMAIN}/rf/report/vehicle?`
      : `${BUMPER_DOMAIN}/${BUILDING_REPORT_PATH}?`;
    window.open(`${base}vin=${vinValue}`, '_blank', 'noopener');
  };

  const handleContactClick = () => {
    trackEvent(routeLocation, 'Click | contact seller | button');
    if (!isAuthenticated) {
      navigate('/login', { state: { next: location.pathname } });
      return;
    }
    setIsContactModalOpen(true);
  };

  const handleContactModalClose = (): void => {
    trackEvent('Lead Form', 'Exit Form');
    setIsContactModalOpen(false);
  };

  const handleContactModalSuccess = (): void => {
    trackEvent('Lead Form', 'Submit Form');
    setIsContactModalOpen(false);
  };

  const handleContactModalError = (message: string): void =>
    trackEvent('Lead Form', `Submit Error | ${message}`);

  const goToLogin = (): void => {
    navigate('/login', { state: { next: location.pathname } });
  };

  const handleSimilarListingClick = (id: string | null | undefined): void => {
    if (id) {
      openListingInNewTab(id);
    }
  };

  const contactButton = listing ? (
    <Button variant="contained" fullWidth onClick={handleContactClick}>
      Check Availability
    </Button>
  ) : undefined;

  const vehicleVin = listing?.vehicle?.vin;

  const selfInspection = vehicleVin ? (
    <>
      {isAuthenticated && (
        <SelfInspectionResults
          vin={vehicleVin}
          vehicleType={{
            vehicleType: listing?.vehicle?.type ?? null,
            vehicleSize: null,
            vehicleDoors: listing?.vehicle?.design?.doors,
          }}
        />
      )}
      <ReportSectionSelfInspectionCTA vin={vehicleVin} />
    </>
  ) : undefined;

  return (
    <Box maxWidth={1290} mx="auto" px={2} py={3}>
      {isLoading ? (
        <Text variant="body1">Loading listing…</Text>
      ) : (
        <>
          <VehicleDetailsPage
            listing={listingWithSaved}
            automobile={automobile}
            similarListings={similarListings}
            isAuthenticated={isAuthenticated}
            backHref={backHref}
            onVinClick={openVinReport}
            onCheckReport={openVinReport}
            onCreateAccount={goToLogin}
            onSimilarListingClick={handleSimilarListingClick}
            onToggleSave={handleToggleSave}
            contactSlot={contactButton}
            selfInspectionSlot={selfInspection}
          />

          <AuthRequiredModal
            open={isAuthRequiredModalOpen}
            onClose={closeAuthRequiredModal}
            onLogin={goToLogin}
            onSignup={goToSignup}
            actionLabel="save this vehicle"
          />

          {listing && isContactModalOpen && (
            <ContactSellerModal
              listing={listing}
              open={isContactModalOpen}
              onClose={handleContactModalClose}
              userFullName={
                userInfo
                  ? `${userInfo.first_name ?? ''} ${userInfo.last_name ?? ''}`.trim()
                  : ''
              }
              userEmail={userInfo?.email ?? ''}
              userCode={userInfo?.user_code ?? ''}
              termsHref={BUMPER_TERMS}
              privacyHref={BUMPER_PRIVACY}
              onSuccess={handleContactModalSuccess}
              onError={handleContactModalError}
            />
          )}
        </>
      )}
    </Box>
  );
}
